import {
  Calendar,
  CopyCheck,
  Hash,
  ListChecks,
  Paperclip,
  Percent,
  Type,
  User,
  Users,
} from "lucide-react";
import { isAttachmentsField, isMultiSelectField } from "@forgepoint/shared";
import type { CustomFieldProperty } from "@forgepoint/shared";

/** The supported custom field type identifiers as a tuple. */
export const CUSTOM_FIELD_TYPES = [
  "string",
  "number",
  "date",
  "user",
  "multiUser",
  "percentage",
  "select",
  "multiSelect",
  "attachments",
] as const;

/** The supported custom field type identifiers. */
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

/** Maps custom field type identifiers to their display labels. */
export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  string: "Text",
  number: "Number",
  percentage: "Percentage",
  date: "Date",
  user: "User",
  multiUser: "Multi-User",
  select: "Select",
  multiSelect: "Multi-Select",
  attachments: "Attachments",
};

/** Maps custom field type identifiers to their corresponding lucide icons. */
export const CUSTOM_FIELD_TYPE_ICONS: Record<CustomFieldType, typeof Type> = {
  string: Type,
  number: Hash,
  percentage: Percent,
  date: Calendar,
  user: User,
  multiUser: Users,
  select: ListChecks,
  multiSelect: CopyCheck,
  attachments: Paperclip,
};

/**
 * Checks if a string value is a valid CustomFieldType.
 * @param value - The string to check
 * @returns True if the value is a valid CustomFieldType
 */
export function isCustomFieldType(value: string): value is CustomFieldType {
  return (CUSTOM_FIELD_TYPES as readonly string[]).includes(value);
}

/** Whether a field type holds org members, synced as HubSpot owners. */
export function isOwnerFieldType(fieldType: CustomFieldType): boolean {
  return fieldType === "user" || fieldType === "multiUser";
}

/**
 * Derives a UI field type from a custom field property definition.
 * @param prop - The custom field property definition, or null
 * @returns The field type identifier
 */
export function deriveCustomFieldType(
  prop: CustomFieldProperty | null,
): CustomFieldType {
  if (!prop) {
    return "string";
  }
  if (prop.enum) {
    return "select";
  }
  if (isMultiSelectField(prop)) {
    return "multiSelect";
  }
  if (prop.type[0] === "array" && prop.format === "fpUser") {
    return "multiUser";
  }
  if (isAttachmentsField(prop)) {
    return "attachments";
  }
  if (prop.type[0] === "number" && prop.format === "percentage") {
    return "percentage";
  }
  if (prop.type[0] === "number") {
    return "number";
  }
  if (prop.format === "date") {
    return "date";
  }
  if (prop.format === "fpUser") {
    return "user";
  }
  return "string";
}
